import {
  useState,
  type FC,
  type ReactNode,
  useRef,
  useEffect,
} from "preact/compat";
import Icons from "@components/icons.ts";
import classnames from 'classnames';
import "./styles/index.scss";

interface AccordionItemProps {
  children: ReactNode;
  allowMultipleAccordionsToBeOpen?: boolean;
  heading: string;
  includeNumberIcons?: boolean;
  isOpen?: boolean;
  modifierClass?: string;
  newStyles?: boolean;
  number?: number;
  rebrand?: boolean;
}

export const AccordionItem: FC<AccordionItemProps> = ({
  children,
  allowMultipleAccordionsToBeOpen = false,
  heading,
  includeNumberIcons = false,
  isOpen = false,
  modifierClass = undefined,
  newStyles = false,
  number,
  rebrand,
}) => {
  const [active, setActive] = useState(isOpen);
  const [maxHeight, setMaxHeight] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      let target = e.target as Element;
      while (target && !target.id.includes("accordion-button")) {
        target = target.parentElement as Element;
      }

      if (!allowMultipleAccordionsToBeOpen && target && target.id !== `accordion-button-${heading}`) {
        setActive(false);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [heading]);

  useEffect(() => {
    if (active && ref.current) {
      const height = ref.current?.scrollHeight;
      return setMaxHeight(height ?? 0);
    }
    setMaxHeight(0);
  }, [active, ref, children]);

  return (
    <div
      className={classnames(`accordion-item Accordion__item w-full px-0 py-2 lg:py-4 border-b border-solid border-gt-black ${modifierClass ?? ''}`, {
        'accordion-item--new-styles': newStyles,
        'accordion-item--rebrand': rebrand,
      })}
    >
      <button
        aria-expanded={active ? "true" : "false"}
        aria-controls={`accordion-content-${heading}`}
        id={`accordion-button-${heading}`}
        className="accordion-item__button Accordion__headingWrap flex w-full cursor-pointer flex-row items-center justify-between border-none text-xl"
        onClick={() => setActive(!active)}
        data-comp-accordion-item-button
      >
        <div className="accordion-item__heading-container">
          {includeNumberIcons && number && (
            <span className="accordion-item__number-icon">
            {number}
          </span>
          )}
          <h3 className="accordion-item__heading styles.Accordion__heading m-0 my-2 text-base font-medium md:text-xl text-left pr-2">
            {heading}
          </h3>
        </div>
        <span
          class={"accordion-item__control border border-solid border-gt-slate-light rounded p-2 lg:p-4"}
        >
          <Icons.PlusRotatingIcon shouldRotate={active} />
        </span>
      </button>
      <div
        aria-labelledby={`accordion-button-${heading}`}
        id={`accordion-content-${heading}`}
        style={{ maxHeight }}
        ref={ref}
        data-state={active ? "active" : "inactive"}
        className="accordion-item__content Accordion__content w-0 overflow-hidden text-left text-base normal-case"
      >
        {children}
      </div>
    </div>
  );
};
